import { Group, Select } from '@byecode/ui'
import { getFlatAllBlock } from '@lighthouse/block'
import type { ScrollToAction } from '@lighthouse/core'
import { type BlockAbstract } from '@lighthouse/core'
import type { NodeTypes, WithBlockOption } from '@lighthouse/shared'
import {
    findBlockById,
    getBlockChildren,
    ListItem4ByecodeUi,
    nodeTypeOptions,
    scroll2FlowNode,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { pageSyncComponentsAtom } from '@/atoms/application/state'
import { blockHighlightAtom, lastPageOfStackAtom, pageBlocksAtom, pageInternalStateAtomFamily } from '@/atoms/page/state'
import type { NodeIdWithScope } from '@/atoms/page/types'

import { ComponentSelector } from '../../Common/ComponentSelector'
import * as SC from '../../styles'

interface ScrollToActionConfigureProps {
    actionTypeSwitcher?: React.ReactNode
    nodeType?: NodeTypes
    value: ScrollToAction['data']['scrollTo']
    onChange: (value: ScrollToAction['data']['scrollTo']) => void
}

export const generateOptions = (pageBlocks: BlockAbstract[], scope?: string) => {
    const blocks = getFlatAllBlock(pageBlocks)
    return blocks.map(block => {
        return {
            label: block.title,
            value: { id: block.id, scope },
            block,
            level: 0
        }
    }) as WithBlockOption[]
}

const collectBlockAndChildrenWithSync = (
    block: BlockAbstract,
    synchronousId: string,
    syncChildren: BlockAbstract[],
    scope?: string
): WithBlockOption[] => {
    const currentIsSyncInstance = block.id === synchronousId
    const children = currentIsSyncInstance ? syncChildren : getBlockChildren(block) ?? []

    return [
        {
            label: block.title,
            value: { id: block.id, scope },
            block,
            level: 0
        },
        ...children.reduce<WithBlockOption[]>((total, curr) => {
            return [
                ...total,
                ...collectBlockAndChildrenWithSync(
                    curr,
                    synchronousId,
                    syncChildren,
                    scope || (currentIsSyncInstance ? block.id : undefined)
                )
            ]
        }, [])
    ]
}

/**
 * 同步组件子级只能获取同属一个作用域内的元素
 */
export const generateOptionsWithSync = (
    currentNode: NodeIdWithScope | undefined,
    blocks: BlockAbstract[],
    syncComponents: BlockAbstract[]
) => {
    if (!currentNode) {
        return generateOptions(blocks)
    }
    if (currentNode.scope) {
        const topBlock = findBlockById(currentNode.scope, blocks)
        if (!topBlock || !topBlock.synchronousId) {
            return
        }

        const masterSyncBlock = syncComponents.find(item => item.id === topBlock.synchronousId)
        if (!masterSyncBlock) {
            return
        }

        const children = getBlockChildren(masterSyncBlock)
        if (!children) {
            return
        }

        return generateOptions(children, currentNode.scope)
    }

    const block = findBlockById(currentNode.id, blocks)
    if (!block) {
        return
    }

    // 是同步组件实例
    if (block.synchronousId) {
        const masterSyncBlock = syncComponents.find(item => item.id === block.synchronousId)
        if (!masterSyncBlock) {
            return
        }
        const syncChildren = getBlockChildren(masterSyncBlock)
        if (!syncChildren) {
            return
        }

        return blocks.reduce<WithBlockOption[]>((total, curr) => {
            return [...total, ...collectBlockAndChildrenWithSync(curr, block.id, syncChildren)]
        }, [])
    }

    return generateOptions(blocks)
}

export const ScrollToActionConfigure: React.FC<ScrollToActionConfigureProps> = ({ actionTypeSwitcher, nodeType, value, onChange }) => {
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const { pageId, stackId } = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => ({ pageId: s?.pageId || '', stackId: s?.stackId || '' }), [])
    )
    const selectedNodes = useAtomData(
        pageInternalStateAtomFamily(stackId),
        useCallback(s => s?.selectedNodes, [])
    )

    const pageBlocks = useAtomData(pageBlocksAtom(pageId))
    const syncComponents = useAtomData(pageSyncComponentsAtom)

    const options = useMemo(() => {
        const currentNode = selectedNodes?.[0]

        return generateOptionsWithSync(currentNode, pageBlocks, syncComponents) ?? []
    }, [pageBlocks, selectedNodes, syncComponents])

    const handleScroll2FlowNode = useCallback(
        (value?: NodeIdWithScope) => {
            setBlockHighlight(value ? [value.id] : [])
            value && scroll2FlowNode(value.id, value.scope)
        },
        [setBlockHighlight]
    )

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
            <Group label="定位">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>目标组件</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <ComponentSelector
                            value={value.scrollToElementId || ''}
                            onChange={node => onChange({ ...value, scrollToElementId: node.id })}
                            options={options}
                            onHover={handleScroll2FlowNode}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
        </SC.Container>
    )
}
